import { useMemo } from 'react'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faClock, faWarning } from '@fortawesome/free-solid-svg-icons'

import { formatDurationHuman } from '@/utils'
import { tz } from '@date-fns/tz'
import { addMilliseconds, differenceInCalendarDays } from 'date-fns'

import { floorMs } from '@/utils/floor-ms'
import singularPlural from '@/utils/singularPlural'

import type { Cue } from '~shared/rundown/cue'
import { CueStartMode } from '~shared/rundown/cue'
import type { TodDisplayFormat } from '~shared/rundown/rundown'
import { formatTimeOfDay, formatTimezone, isSameDay } from '~shared/timeutils'
import { RunnerState } from '~shared/types'
import type { Timestamp } from '~shared/utils'
import { CueRunState } from '~shared/utils'

export type TimingInfo = {
  type?: 'Gap' | 'Overlap'
  text?: string
  textColor?: string
  bgColor?: string
  icon?: IconDefinition | null
  timeDiffStr?: string
  differenceMs?: number
}

export function useTimingInfo(
  cue: Cue | undefined,
  previousCueTimestamp: Timestamp | null | undefined,
  timestamp: Timestamp | null | undefined,
  timezone: string,
): TimingInfo {
  if (!cue || !previousCueTimestamp || !timestamp) return {}

  const previousEndTime = addMilliseconds(
    previousCueTimestamp.actual.start,
    previousCueTimestamp.actual.duration,
  )
  const differenceMs =
    floorMs(timestamp.actual.start).getTime() -
    floorMs(previousEndTime).getTime()
  const timeDiffStr =
    differenceMs === 0
      ? 'On time'
      : formatDurationHuman(differenceMs, { prefix: '' })

  let type: TimingInfo['type']
  let text = ''
  let textColor = ''
  let bgColor = ''
  let icon: IconDefinition | null = null

  if (cue.startMode === CueStartMode.FIXED) {
    if (differenceMs > 0) {
      type = 'Gap'
      text = `Gap of ${timeDiffStr}`
      textColor = 'text-green-600'
      bgColor = 'bg-green-400'
      icon = faClock
    } else if (differenceMs < 0) {
      type = 'Overlap'
      text = `Overlaps by ${timeDiffStr}`
      textColor = 'text-red-600'
      bgColor = 'bg-red-400'
      icon = faWarning
    }
  }

  if (
    !isSameDay(timestamp.actual.start, previousCueTimestamp.actual.start, {
      timezone,
    })
  ) {
    if (timestamp.actual.start > previousCueTimestamp.actual.start) {
      if (!text) text = 'Crossing midnight'
      else text = `${text} and crossing midnight`
    }
  }

  return { type, text, textColor, bgColor, icon, timeDiffStr, differenceMs }
}

const dateFormatter = new Intl.DateTimeFormat(navigator.language, {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

type UseTimingDescriptionArgs = {
  timestamp?: Timestamp | null
  startTime?: Date | null
  startMode?: CueStartMode
  scheduled?: boolean
  timezone?: string
  runnerState?: RunnerState
  todDisplayFormat?: TodDisplayFormat | null
  rundownStartTime?: Date
}

/**
 * Hook that provides formatted timing descriptions for cues
 */
export function useTimingDescription({
  timestamp,
  startTime,
  startMode = CueStartMode.FLEXIBLE,
  scheduled = false,
  timezone = 'UTC',
  runnerState = RunnerState.PRESHOW,
  todDisplayFormat = null,
  rundownStartTime,
}: UseTimingDescriptionArgs) {
  return useMemo(() => {
    // Early return if no valid timing info
    if (!timestamp && !startTime) return ''

    // Get basic formatting elements used by both states
    const tzAbbr = formatTimezone(timezone, 'abbr')

    if (runnerState === RunnerState.PRESHOW) {
      return formatPreshowTiming({
        startTime: startTime ?? timestamp?.actual?.start,
        startMode,
        scheduled,
        timezone,
        todDisplayFormat,
        tzAbbr,
        rundownStartTime,
      })
    }

    return formatRuntimeTiming({
      timestamp,
      scheduled,
      timezone,
      todDisplayFormat,
      tzAbbr,
    })
  }, [
    timestamp,
    startTime,
    startMode,
    scheduled,
    timezone,
    runnerState,
    todDisplayFormat,
    rundownStartTime,
  ])
}

/**
 * Formats timing description during the planning phase (PRESHOW)
 */
function formatPreshowTiming({
  startTime,
  startMode,
  scheduled,
  timezone,
  todDisplayFormat,
  tzAbbr,
  rundownStartTime,
}: {
  startTime?: Date
  startMode: CueStartMode
  scheduled: boolean
  timezone: string
  todDisplayFormat: TodDisplayFormat | null
  tzAbbr: string
  rundownStartTime?: Date
}) {
  if (!startTime) return ''

  const timeStr = formatTimeOfDay(startTime, {
    timezone,
    seconds: 'nonzero',
    format: todDisplayFormat,
  })

  const dateStr = dateFormatter.format(startTime)
  const modeStr = startMode === CueStartMode.FIXED ? 'hard' : 'soft'
  const startStr = scheduled ? 'auto-start' : 'start'

  // Days offset display
  const daysPlus = rundownStartTime
    ? differenceInCalendarDays(startTime, rundownStartTime, {
        in: tz(timezone),
      })
    : 0
  const daysOffsetStr =
    daysPlus > 0 ? '+' + singularPlural(daysPlus, 'day', 'days') : ''

  // In planning mode, include date for hard cues only
  const parens = [tzAbbr, daysOffsetStr]
  if (startMode === CueStartMode.FIXED) {
    parens.unshift(dateStr)
  }

  return `Planned ${modeStr} ${startStr} at ${timeStr} (${_arrJoin(parens)}).`.replaceAll(
    '  ',
    ' ',
  )
}

/**
 * Formats timing description during show runtime (ONAIR) and after show (ENDED)
 */
function formatRuntimeTiming({
  timestamp,
  scheduled,
  timezone,
  todDisplayFormat,
  tzAbbr,
}: {
  timestamp?: Timestamp | null
  scheduled: boolean
  timezone: string
  todDisplayFormat: TodDisplayFormat | null
  tzAbbr: string
}) {
  if (!timestamp) return ''

  const timeStr = formatTimeOfDay(timestamp.actual.start, {
    timezone,
    seconds: 'nonzero',
    format: todDisplayFormat,
  })

  // Days offset display (if any)
  const daysOffsetStr =
    timestamp.actual.daysPlus > 0
      ? `+${timestamp.actual.daysPlus} ${timestamp.actual.daysPlus === 1 ? 'day' : 'days'}`
      : ''

  // Timing state prefix
  const prefix =
    timestamp.state === CueRunState.CUE_PAST ||
    timestamp.state === CueRunState.CUE_ACTIVE
      ? 'Started'
      : 'Expected to'

  const startStr = scheduled ? 'auto-start' : 'start'

  // Calculate time difference from planned schedule
  let diffStr = ''
  if (timestamp.original.start) {
    const diffMs =
      timestamp.actual.start.getTime() - timestamp.original.start.getTime()
    if (diffMs === 0) {
      diffStr = 'right on time'
    } else {
      const diffMinutes = Math.abs(Math.round(diffMs / 60000))
      const diffHours = Math.floor(diffMinutes / 60)
      const remainingMinutes = diffMinutes % 60

      let diffTimeStr = ''
      if (diffHours > 0) {
        diffTimeStr += `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`
        if (remainingMinutes > 0) {
          diffTimeStr += ` ${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`
        }
      } else {
        diffTimeStr = `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'}`
      }

      diffStr =
        diffMs > 0
          ? `${diffTimeStr} later than planned`
          : `${diffTimeStr} earlier than planned`
    }
  }

  // Compose final string
  const parens = [tzAbbr, daysOffsetStr]
  return `${prefix} ${startStr} at ${timeStr} (${_arrJoin(parens)})${diffStr ? ', ' + diffStr : ''}.`.replaceAll(
    '  ',
    ' ',
  )
}

/**
 * Joins array elements with commas and replaces spaces with non-breaking spaces
 * within each element to prevent unwanted line breaks
 */
function _arrJoin(arr: (string | undefined)[]) {
  return arr
    .filter(Boolean)
    .map((str) => str!.replace(/ /g, '\u00A0'))
    .join(', ')
}
